import React, {
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  JSX,
} from 'react';
import { IDeliveryAddress } from '../models/requests/omm';
import {
  IAvailableDeliverySlot,
  IAvailableDeliverySlotResponse,
} from '../models/edit-delivery-address';

// ******* TYPES ******
export interface IEditDeliveryAddressContext {
  formState: AddressFormState;
  updateFormField: (fields: Partial<AddressFormState>) => void;
  deliveryAddresses: IDeliveryAddress[];
  setDeliveryAddresses: Dispatch<SetStateAction<IDeliveryAddress[]>>;
  requireSignature: boolean;
  setRequireSignature: Dispatch<SetStateAction<boolean>>;
  showCalendar: boolean;
  setShowCalendar: Dispatch<SetStateAction<boolean>>;
  availableDeliverySlotsResponse: IAvailableDeliverySlotResponse | null;
  setAvailableDeliverySlotsResponse: Dispatch<
    SetStateAction<IAvailableDeliverySlotResponse | null>
  >;
  selectedDateId: string | null;
  setSelectedDateId: Dispatch<SetStateAction<string | null>>;
  availableDeliverySlots: IAvailableDeliverySlot[];
  setAvailableDeliverySlots: Dispatch<SetStateAction<IAvailableDeliverySlot[]>>;
  deliveryDate: string;
  setDeliveryDate: Dispatch<SetStateAction<string>>;
  selectedDeliverySlot: IAvailableDeliverySlot | null;
  setSelectedDeliverySlot: Dispatch<
    SetStateAction<IAvailableDeliverySlot | null>
  >;
  isEligibilityChecked: boolean;
  setIsEligibilityChecked: Dispatch<SetStateAction<boolean>>;
  isEligible: boolean;
  setIsEligible: Dispatch<SetStateAction<boolean>>;
  selectedAddressId: string;
  setSelectedAddressId: Dispatch<SetStateAction<string>>;
}

interface IEditDeliveryAddressProvider {
  children: JSX.Element;
}

export type AddressFormState = {
  firstNameValue: string;
  lastNameValue: string;
  streetAddressValue: string;
  addressLineTwoValue: string | null;
  zipCodeValue: string;
  cityStateValue: string;
  phoneNumberValue: string;
};

// ******* CONTEXT ******
export const EditDeliveryAddressContext = createContext<
  IEditDeliveryAddressContext | undefined
>(undefined);

// ******* PROVIDER ******
export const EditDeliveryAddressProvider = ({
  children,
}: IEditDeliveryAddressProvider) => {
  const [formState, setFormState] = useState<AddressFormState>({
    firstNameValue: '',
    lastNameValue: '',
    streetAddressValue: '',
    addressLineTwoValue: null,
    zipCodeValue: '',
    cityStateValue: '',
    phoneNumberValue: '',
  });

  const [deliveryAddresses, setDeliveryAddresses] = useState<
    IDeliveryAddress[]
  >([]);
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [requireSignature, setRequireSignature] = useState<boolean>(false);
  const [availableDeliverySlotsResponse, setAvailableDeliverySlotsResponse] =
    useState<IAvailableDeliverySlotResponse | null>(null);
  const [selectedDateId, setSelectedDateId] = useState<string | null>(null);
  const [availableDeliverySlots, setAvailableDeliverySlots] = useState<
    IAvailableDeliverySlot[]
  >([]);
  const [deliveryDate, setDeliveryDate] = useState<string>('');
  const [selectedDeliverySlot, setSelectedDeliverySlot] =
    useState<IAvailableDeliverySlot | null>(null);
  const [isEligibilityChecked, setIsEligibilityChecked] =
    useState<boolean>(false);
  const [isEligible, setIsEligible] = useState<boolean>(false);
  const [selectedAddressId, setSelectedAddressId] = useState<string>('');

  const updateFormField = (fields: Partial<AddressFormState>) => {
    setFormState((prev) => ({
      ...prev,
      ...fields,
    }));
  };

  return (
    <EditDeliveryAddressContext.Provider
      value={{
        formState,
        updateFormField,
        deliveryAddresses,
        setDeliveryAddresses,
        showCalendar,
        setShowCalendar,
        requireSignature,
        setRequireSignature,
        availableDeliverySlots,
        setAvailableDeliverySlots,
        availableDeliverySlotsResponse,
        setAvailableDeliverySlotsResponse,
        selectedDateId,
        setSelectedDateId,
        deliveryDate,
        setDeliveryDate,
        selectedDeliverySlot,
        setSelectedDeliverySlot,
        isEligibilityChecked,
        setIsEligibilityChecked,
        isEligible,
        setIsEligible,
        selectedAddressId,
        setSelectedAddressId,
      }}
    >
      {children}
    </EditDeliveryAddressContext.Provider>
  );
};
